/**
 * How a `wikiLink` looks and behaves on screen (`070` §7.2, `031-content-format.md`).
 *
 * **Resolution happens HERE, at render time** — not in the document, and never by rewriting
 * it (`070` §7.4; the argument is in `wikiLinkResolve.ts`). The node carries `pageId` and
 * `label`; what the user SEES is computed from the page tree that is already in the
 * TanStack Query cache. Consequences, both of them acceptance criteria:
 *
 *  - Rename a page and every link to it shows the new title immediately (criterion 10).
 *  - Create a page and every `[[…]]` that was waiting for it lights up (criterion 9).
 *
 * A broken link is a **first-class, expected state**, not an error. It is dashed and muted,
 * with a `Plus` on hover: an invitation, not a failure. Never red. Never a warning icon.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react';
import { keys } from '@/api/keys';
import { getTree } from '@/api/endpoints/pages';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useWikiLinkActions } from '@/features/editor/wikiLinkContext';
import { buildWikiLinkIndex, resolveWikiLink } from '@/features/editor/wikiLinkResolve';

export function WikiLinkNodeView({ node, selected }: ReactNodeViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = useWikiLinkActions();

  // The SAME key and fetcher as `useTree` — so this is a cache read, not a second request.
  // "The client already has the page list… the lookup is free" (`070` §7.4, reason 3).
  const tree = useQuery({
    queryKey: keys.pages.tree(),
    queryFn: getTree,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const pages = tree.data?.pages;
  const index = useMemo(() => buildWikiLinkIndex(pages ?? []), [pages]);

  const attrs = node.attrs as { pageId: string | null; label: string };
  const link = useMemo(() => resolveWikiLink(attrs, index), [attrs, index]);

  const activate = (): void => {
    if (link.targetId !== null) {
      void navigate(`/p/${link.targetId}`);
      return;
    }
    // "Clicking an unresolved link offers to create that page" (`031`). The page is created
    // as a child of the one we are on; §7.4 then resolves the graph server-side.
    actions?.createPage(link.text);
  };

  return (
    <NodeViewWrapper as="span" className="inline">
      <a
        // Not an `href`: navigation is the router's, and a real href would let a
        // middle-click open a URL the SPA has to re-boot to serve. The role/tabIndex keep
        // it reachable and operable from the keyboard, which an <a> without href is not.
        role="link"
        tabIndex={0}
        data-page-id={link.targetId ?? undefined}
        data-broken={link.broken ? 'true' : undefined}
        data-testid="wiki-link"
        title={link.broken ? t('editor.wikiLink.brokenTitle', { text: link.text }) : link.text}
        className={cn(
          'wiki-link group cursor-pointer',
          link.broken && 'wiki-link--broken',
          selected && 'rounded-[2px] bg-[var(--selection)]',
        )}
        onClick={(event) => {
          event.preventDefault();
          activate();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          activate();
        }}
      >
        {link.text}
        {link.broken && (
          // The affordance, on hover only — a permanent icon on every unwritten link would
          // make a page full of them look like a page full of errors.
          <Plus
            aria-hidden
            className="ml-0.5 inline size-3 align-[-1px] opacity-0 transition-opacity group-hover:opacity-100"
            strokeWidth={2}
          />
        )}
      </a>
    </NodeViewWrapper>
  );
}
